import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PDFDocument, PDFPage, PDFPageDrawRectangleOptions, rgb } from 'pdf-lib';
import { SearchWordService } from '@shared/services/search-word.service';
export interface WordPositionGroup {
  word: string;
  positions: WordPosition[];
}
export interface WordPosition {
  pageNumber: number;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}
@Injectable({
  providedIn: 'root',
})
export class PdfViewerAdapterService {
  private readonly highlightScale = 2;
  pdfUrl: string;

  constructor(private pdfApiService: SearchWordService) {}
  public highlightWordsFromCandidateFilters(
    arrayBuffer: ArrayBuffer,
    pdfId: number,
    words: string[],
    wholeWords: boolean,
  ): Observable<ArrayBuffer> {
    return this.getFilterWordsPositionInResume(pdfId, words, wholeWords).pipe(
      switchMap((groups) => from(this.highlightWordGroups(groups, arrayBuffer))),
    );
  }

  private getFilterWordsPositionInResume(
    pdfId: number,
    words: string[],
    wholeWords: boolean,
  ): Observable<WordPositionGroup[]> {
    if (words?.length > 0) {
      return this.pdfApiService
        .searchWords(pdfId, words, wholeWords)
        .pipe(catchError(() => of([])));
    }
  }

  private async highlightWordGroups(
    groups: WordPositionGroup[],
    arrayBuffer: ArrayBuffer,
  ): Promise<ArrayBuffer> {
    if (!groups || groups.length === 0) {
      return arrayBuffer;
    }
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    groups.forEach((group) => {
      group.positions?.forEach((position) => {
        const page = pages[position.pageNumber - 1];
        this.drawRectangle(position, page);
      });
    });
    return await pdfDoc.save();
  }

  private drawRectangle(wordPosition: WordPosition, page: PDFPage) {
    page.drawRectangle(this.getScaledDrawRectangle(wordPosition, page));
  }

  private getScaledDrawRectangle(
    wordPosition: WordPosition,
    page: PDFPage,
  ): PDFPageDrawRectangleOptions {
    const height = this.highlightScale * wordPosition.height;
    const y =
      page.getHeight() - wordPosition.positionY - 0.75 * this.highlightScale * wordPosition.height;
    const color = rgb(0.5, 0.76, 0.98);
    return {
      x: wordPosition.positionX,
      y,
      width: wordPosition.width,
      height,
      color,
      opacity: 0.3,
    };
  }
}
